/**
 * Max-heap of Int32 values, ordered by an external priority lookup table.
 *
 * Each value pushed is itself an index into the externally provided `priorities` array — i.e.
 * the priority of value `v` while it sits in the heap is `priorities[v]`. The largest-priority
 * element is always at the root (index 0 of the buffer).
 *
 * The backing buffer is supplied by the caller. This lets the heap share storage with another
 * region of the same `Int32Array` (e.g. {@link ModelIteratorBVH} uses the same buffer as both a
 * heap and a LIFO stack, since the two are never simultaneously non-empty). The heap's working
 * region is always `[0, size)` of the buffer; the caller is responsible for ensuring the buffer
 * is large enough for the maximum number of simultaneously-stored elements.
 *
 * `buffer` and `size` are intentionally public so that callers can prime the heap with raw writes
 * without paying for `heapifyUp` (e.g. when seeding multiple roots whose relative order is fixed
 * by convention rather than priority) and so tests can inspect state directly.
 *
 * Priorities are read at the time of comparison. If a value's priority changes while it is in the
 * heap, the heap property may be violated; callers must either avoid mutating in-flight priorities
 * or re-heapify externally.
 */
export class Int32MaxHeap {
  /**
   * @param {Int32Array} buffer - Backing storage. Heap occupies `[0, size)`.
   * @param {Float32Array | Int32Array | Array<number>} priorities - Priority lookup. `priorities[v]`
   *     gives the priority of value `v`. Values pushed must be valid indices into this array.
   */
  constructor(buffer, priorities) {
    this.buffer = buffer;
    this.priorities = priorities;
    this.size = 0;
  }

  /** Reset to empty without releasing the backing buffer. */
  clear() {
    this.size = 0;
  }

  /**
   * Adds a value. The caller is responsible for ensuring `priorities[value]` is up to date
   * before pushing. Behavior is undefined if `size === buffer.length` already.
   * @param {number} value
   */
  push(value) {
    this.#siftUp(this.size++, value);
  }

  /**
   * Remove and return the value with the largest priority.
   * Caller must ensure the heap is non-empty (use `size === 0` to check).
   * @returns {number} The popped value.
   */
  pop() {
    const buffer = this.buffer;
    const result = buffer[0];
    const newSize = --this.size;
    if (newSize > 0) {
      this.#siftDown(0, buffer[newSize]);
    }
    return result;
  }

  /**
   * Place `value` at the slot at `idx`, sifting/bubbling it up toward the root.
   * @private
   */
  #siftUp(idx, value) {
    const buffer = this.buffer;
    const priorities = this.priorities;
    const valuePriority = priorities[value];

    while (idx > 0) {
      const parentIdx = idx - 1 >> 1;
      const parent = buffer[parentIdx];
      if (priorities[parent] >= valuePriority) {
        break;
      }
      buffer[idx] = parent;
      idx = parentIdx;
    }
    buffer[idx] = value;
  }

  /**
   * Place `value` into the subtree rooted at `idx`, restoring the heap property.
   *
   * Implementation uses Floyd's bottom-up sift-down: descend from `idx` to a leaf always
   * picking the larger child (1 comparison per level, no comparison against `value`), then
   * sift `value` back up from that leaf (1 comparison per level traversed back up).
   * @private
   */
  #siftDown(idx, value) {
    const buffer = this.buffer;
    const priorities = this.priorities;
    const size = this.size;
    const startIdx = idx;

    // Descent: walk to a leaf, always taking the larger child.
    while (true) {
      const left = (idx << 1) + 1;
      if (left >= size) {
        break;
      }
      const right = left + 1;
      let childIdx = left;
      if (right < size && priorities[buffer[right]] > priorities[buffer[left]]) {
        childIdx = right;
      }
      buffer[idx] = buffer[childIdx];
      idx = childIdx;
    }

    // Sift `value` back up toward `startIdx`. On average this terminates within O(1) steps.
    // Note this duplicates #siftUp but bounds at `startIdx` rather than the root.
    // I picked duplication over making siftUp more general.
    const valuePriority = priorities[value];
    while (idx > startIdx) {
      const parentIdx = idx - 1 >> 1;
      const parent = buffer[parentIdx];
      if (priorities[parent] >= valuePriority) {
        break;
      }
      buffer[idx] = parent;
      idx = parentIdx;
    }
    buffer[idx] = value;
  }
}